import { useState, useEffect } from 'react';
import '../styles/Deadlines.css';
import { ArrowLeft, ArrowRight, SquarePen } from 'lucide-react';
import Alert from './Alert';
import { CalendarEntryModal } from './CalendarEntryModal';

export const DeadlineCreation = ({ case_id }) => {
    const [deadlines, setDeadlines] = useState([]);
    const [calendarDrafts, setCalendarDrafts] = useState([]);
    const [deadline, setDeadline] = useState({
        users: [],
        subject: '',
        date: '',
        time: '',
        endDate: '',
        endTime: '',
        notes: '',
        isAllDay: true,
        reminders: [],
        defaultReminders: true,
        files: [],
        calendarStatus: 'pushed'
    });
    const [editingDeadline, setEditingDeadline] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [creatingDeadline, setCreatingDeadline] = useState(false);
    const [activeDay, setActiveDay] = useState(null);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [submitting, setSubmitting] = useState(false);
    const [caseTeamEmails, setCaseTeamEmails] = useState({});

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const days = [];

        const firstDay = new Date(year, month, 1);
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const startDayOfWeek = firstDay.getDay();

        for (let i = 0; i < startDayOfWeek; i++) {
            days.push({ blank: true });
        }

        for (let i = 1; i <= daysInMonth; i++) {
            const currentDate = new Date(year, month, i);
            days.push({
                date: currentDate.toLocaleDateString('en-CA'),
                dayName: currentDate.toLocaleDateString('en-US', { weekday: 'short' }),
                dayNumber: i
            });
        }

        return days;
    };

    const daysInMonth = getDaysInMonth(currentMonth);

    const normalizeDateTime = (value) => {
        if (!value || typeof value !== 'string') return null;
        return value.includes('T') ? value : value.replace(' ', 'T');
    };

    const normalizeCalendarUsers = (entry) => {
        if (Array.isArray(entry?.emails_used)) return entry.emails_used.filter(Boolean);
        if (typeof entry?.emails_used === 'string') return entry.emails_used.split(',').map(email => email.trim()).filter(Boolean);
        if (Array.isArray(entry?.users)) return entry.users.map(user => user?.email || user).filter(Boolean);
        return [];
    };

    const getEntryDate = (entry) => {
        const startDate = normalizeDateTime(entry?.start_date);
        return startDate ? startDate.split('T')[0] : entry?.deadline_date || null;
    };

    const datedCalendarDrafts = calendarDrafts.filter(draft => getEntryDate(draft));
    const undatedCalendarDrafts = calendarDrafts.filter(draft => !getEntryDate(draft));
    const calendarEntries = [...deadlines, ...datedCalendarDrafts].sort((left, right) => {
        const leftDate = normalizeDateTime(left?.start_date) || `${left?.deadline_date || ''}T00:00:00`;
        const rightDate = normalizeDateTime(right?.start_date) || `${right?.deadline_date || ''}T00:00:00`;
        return leftDate.localeCompare(rightDate);
    });
    const getEntriesForDate = (date) => calendarEntries.filter(entry => getEntryDate(entry) === date);

    const formatEntryDateTime = (entry) => {
        const startDate = normalizeDateTime(entry?.start_date);
        if (!startDate) return 'Date needed';
        const date = new Date(startDate);
        if (Number.isNaN(date.getTime())) return startDate.split('T')[0];
        if (Number(entry?.isAllDay) === 1 || entry?.is_draft) {
            return date.toLocaleDateString();
        }
        return [date.toLocaleDateString(), date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })].join(', ');
    };

    const renderCalendarEntry = (entry, key) => {
        const isDraft = entry?.is_draft || entry?.source === 'case_calendar_draft';
        const users = Array.isArray(entry?.users) ? entry.users.filter(Boolean) : [];
        const reminderText = typeof entry?.reminders === 'string'
            ? entry.reminders.split(',').filter(Boolean).join('/')
            : Array.isArray(entry?.reminders) ? entry.reminders.filter(Boolean).join('/') : '';
        const confidence = entry?.confidence != null ? `${Math.round(Number(entry.confidence) * 100)}%` : null;

        return (
            <div key={key} className={`deadline-item ${isDraft ? 'deadline-draft-item' : ''}`}>
                <div className='deadline-entry-main'>
                    <div className='deadlines-title'>
                        {entry.calendar_title}
                        {isDraft && <span className='deadline-draft-badge'>Draft</span>}
                    </div>
                    <div className='deadline-detail subtext'>
                        <span className='deadline-date-time'>
                            <div className='deadlines-date'>{formatEntryDateTime(entry)}</div>
                        </span>
                        {isDraft ? (
                            <>
                                <div className='deadline-users'>{entry.event_type?.replaceAll('_', ' ') || 'Calendar draft'}{confidence ? ` · ${confidence}` : ''}</div>
                                {entry.source_file_name && <div className='deadline-reminders'>Source: {entry.source_file_name}</div>}
                            </>
                        ) : (
                            <>
                                <div className='deadline-users' title={users[0]?.email || ''}>{users[0]?.name || users[0]?.email || 'No user'}{users.length > 1 ? ` + ${users.length - 1} more` : ''}</div>
                                {reminderText && <div className='deadline-reminders'>{reminderText.includes('/') ? 'Reminders' : 'Reminder'}: {reminderText}</div>}
                            </>
                        )}
                    </div>
                    {isDraft && entry.source_excerpt && <div className='deadline-draft-excerpt subtext'>{entry.source_excerpt}</div>}
                </div>
                <div className='deadline-actions' onClick={() => handleEditDeadline(entry)} title={isDraft ? 'Edit draft' : 'Edit calendar entry'}>
                    <SquarePen size={18} />
                </div>
            </div>
        );
    };

    const fetchDeadlines = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/deadlines.php?case_id=${case_id}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            if (data.success) {
                setDeadlines(Array.isArray(data.deadlines) ? data.deadlines : []);
                setCalendarDrafts(Array.isArray(data.calendar_drafts) ? data.calendar_drafts : []);
            } else {
                console.error("Error fetching deadlines:", data.error);
            }
        } catch (error) {
            console.error("Error fetching deadlines:", error);
        }
    }

    const addDeadlineCalendarEvent = async () => {
        setSubmitting(true);
        
        try {
            let startDateTime = null;
            let endDateTime = null;
            
            if (!deadline.isAllDay) {
                startDateTime = `${deadline.date}T${deadline.time}:00`;
                // Use end date/time if provided, otherwise default to start date/time
                if (deadline.endDate && deadline.endTime) {
                    endDateTime = `${deadline.endDate}T${deadline.endTime}:00`;
                } else {
                    endDateTime = startDateTime; // Default to same as start
                }
            } else {
                startDateTime = `${deadline.date}T00:00:00`;
                endDateTime = startDateTime;
            }

            const formData = new FormData();
            formData.append('case_id', case_id);
            formData.append('subject', deadline.subject);
            formData.append('notes', deadline.notes);
            formData.append('reminders', JSON.stringify(deadline.reminders.length > 0 ? deadline.reminders : deadline.defaultReminders ? [30, 60, 90] : []));
            formData.append('users', JSON.stringify(deadline.users));
            formData.append('deadline', JSON.stringify({
                start: startDateTime,
                end: endDateTime
            }));
            formData.append('is_all_day', deadline.isAllDay);
            deadline.files.forEach(file => {
                formData.append('files[]', file);
            });

            if (deadline.calendarStatus === 'draft') {
                const response = await fetch('https://api.casedb.co/deadlines.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        calendar_status: 'draft',
                        users: deadline.users,
                        subject: deadline.subject,
                        start_date: startDateTime,
                        end_date: endDateTime,
                        notes: deadline.notes,
                        isAllDay: deadline.isAllDay,
                        reminders: deadline.reminders.length > 0 ? deadline.reminders : deadline.defaultReminders ? [30, 60, 90] : [],
                        case_id,
                        files: deadline.files
                    })
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                if (data.success) {
                    setCreatingDeadline(false);
                    fetchDeadlines();
                    setMessage({ text: 'Calendar draft saved successfully!', type: 'success' });
                    setDeadline({
                        users: [],
                        subject: '',
                        date: '',
                        time: '',
                        endDate: '',
                        endTime: '',
                        notes: '',
                        isAllDay: true,
                        reminders: [],
                        defaultReminders: true,
                        files: [],
                        isDraft: false,
                        draft_id: null,
                        calendarStatus: 'pushed'
                    });
                    setSubmitting(false);
                } else {
                    setMessage({ text: `Error creating calendar draft: ${data.error || 'Unknown error occurred'}`, type: 'error' });
                    setSubmitting(false);
                }
                return;
            }

            const response = await fetch('https://api.casedb.co/create-event.php?case=true', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
        
        // Check if there are any errors in the results array
        let hasErrors = false;
        let errorMessage = '';
        
        if (data.results && Array.isArray(data.results)) {
            for (const result of data.results) {
                if (result.error) {
                    hasErrors = true;
                    errorMessage = result.error.message || result.error.code || 'Unknown error occurred';
                    break;
                }
            }
        }
        
        if (data.success && !hasErrors) {
            setCreatingDeadline(false);
            fetchDeadlines();
            setMessage({ text: 'Deadline created successfully!', type: 'success' });
            setDeadline({
                users: [],
                subject: '',
                date: '',
                time: '',
                endDate: '',
                endTime: '',
                notes: '',
                isAllDay: true,
                reminders: [],
                defaultReminders: true,
                files: [],
                calendarStatus: 'pushed'
            });
            setSubmitting(false);
        } else {
            const finalErrorMessage = hasErrors ? errorMessage : (data.error || 'Unknown error occurred');
            console.error("Error creating deadline event:", finalErrorMessage);
            setMessage({ text: `Error creating deadline event: ${finalErrorMessage}`, type: 'error' });
            setSubmitting(false);
        }
        } catch (error) {
            console.error("Network or parsing error:", error);
            setMessage({ text: `Network error: ${error.message}`, type: 'error' });
            setSubmitting(false);
        }
    };

    const handleUpdateDeadline = async () => {
        setSubmitting(true);
        
        try {
            let startDateTime, endDateTime;
        
        if (!deadline.isAllDay) {
            startDateTime = `${deadline.date}T${deadline.time}:00`;
            // Use end date/time if provided, otherwise default to start date/time
            if (deadline.endDate && deadline.endTime) {
                endDateTime = `${deadline.endDate}T${deadline.endTime}:00`;
            } else {
                endDateTime = startDateTime; // Default to same as start
            }
        } else {
            startDateTime = `${deadline.date}T00:00:00`;
            endDateTime = startDateTime;
        }

        const response = await fetch('https://api.casedb.co/deadlines.php', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                event_uuid: deadline.event_uuid,
                draft_id: deadline.draft_id,
                calendar_status: deadline.calendarStatus || (deadline.isDraft ? 'draft' : 'pushed'),
                users: deadline.users,
                subject: deadline.subject,
                start_date: startDateTime,
                end_date: endDateTime,
                notes: deadline.notes,
                isAllDay: deadline.isAllDay,
                reminders: deadline.reminders.length > 0 ? deadline.reminders : deadline.defaultReminders ? [30, 60, 90] : [],
                case_id: case_id,
                files: deadline.files
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        // Check if there are any errors in the results array  
        let hasErrors = false;
        let errorMessage = '';
        
        if (data.results && Array.isArray(data.results)) {
            for (const result of data.results) {
                if (result.error) {
                    hasErrors = true;
                    errorMessage = result.error.message || result.error.code || 'Unknown error occurred';
                    break;
                }
            }
        }
        
        if (data.success && !hasErrors) {
            console.log("Deadline event updated successfully:", data);
            setEditingDeadline(false);
            fetchDeadlines();
            setMessage({ text: deadline.calendarStatus === 'pushed' && deadline.isDraft ? 'Calendar draft pushed successfully!' : deadline.calendarStatus === 'draft' && !deadline.isDraft ? 'Calendar entry moved back to draft.' : deadline.isDraft ? 'Calendar draft updated successfully!' : 'Deadline updated successfully!', type: 'success' });
            setDeadline({
                users: [],
                subject: '',
                date: '',
                time: '',
                endDate: '',
                endTime: '',
                notes: '',
                isAllDay: true,
                reminders: [],
                defaultReminders: true,
                files: [],
                isDraft: false,
                draft_id: null,
                calendarStatus: 'pushed'
            });
            setSubmitting(false);
        } else {
            const finalErrorMessage = hasErrors ? errorMessage : (data.error || 'Unknown error occurred');
            console.error("Error updating deadline event:", finalErrorMessage);
            setMessage({ text: `Error updating deadline event: ${finalErrorMessage}`, type: 'error' });
            setSubmitting(false);
        }
        } catch (error) {
            console.error("Network or parsing error:", error);
            setMessage({ text: `Network error: ${error.message}`, type: 'error' });
            setSubmitting(false);
        }
    };

    const handleEditDeadline = (deadline) => {
        const isDraft = deadline?.is_draft || deadline?.source === 'case_calendar_draft';
        const startDate = normalizeDateTime(deadline?.start_date);
        const endDate = normalizeDateTime(deadline?.end_date) || startDate;
        const entryUsers = normalizeCalendarUsers(deadline);
        setDeadline({
            isDraft,
            draft_id: deadline.draft_id || null,
            event_uuid: deadline.event_uuid,
            users: entryUsers,
            subject: deadline.calendar_title,
            date: startDate?.split("T")[0] || deadline?.deadline_date || '',
            time: startDate?.split("T")[1]?.substring(0, 5) || '',
            endDate: endDate?.split("T")[0] || startDate?.split("T")[0] || deadline?.deadline_date || '',
            endTime: endDate?.split("T")[1]?.substring(0, 5) || startDate?.split("T")[1]?.substring(0, 5) || '',
            notes: deadline?.notes,
            isAllDay: Number(deadline?.isAllDay) === 1 ? true : false,
            calendarStatus: isDraft ? 'draft' : 'pushed',
            reminders: typeof deadline?.reminders === 'string'
                ? deadline.reminders.split(',').map(r => parseInt(r, 10))
                : deadline.reminders,
            defaultReminders: deadline.reminders === '30,60,90',
            files: deadline.files
        });
        setEditingDeadline(true);
    };

    const handleDeleteDeadline = async (event_uuid) => {
        setSubmitting(true);
        
        try {
        const response = await fetch('https://api.casedb.co/deadlines.php', {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ event_uuid, draft_id: deadline.draft_id })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        // Check if there are any errors in the results array
        let hasErrors = false;
        let errorMessage = '';
        
        if (data.results && Array.isArray(data.results)) {
            for (const result of data.results) {
                if (result.error) {
                    hasErrors = true;
                    errorMessage = result.error.message || result.error.code || 'Unknown error occurred';
                    break;
                }
            }
        }
        
        if (data.success && !hasErrors) {
            setEditingDeadline(false);
            setMessage({ text: deadline.isDraft ? 'Calendar draft deleted successfully!' : 'Deadline deleted successfully!', type: 'success' });
            setDeadline({
                users: [],
                subject: '',
                date: '',
                time: '',
                endDate: '',
                endTime: '',
                notes: '',
                isAllDay: true,
                reminders: [],
                defaultReminders: true,
                files: [],
                isDraft: false,
                draft_id: null,
                calendarStatus: 'pushed'
            });
            fetchDeadlines();
            setSubmitting(false);
        } else {
            const finalErrorMessage = hasErrors ? errorMessage : (data.error || 'Unknown error occurred');
            console.error("Error deleting deadline event:", finalErrorMessage);
            setMessage({ text: `Error deleting deadline event: ${finalErrorMessage}`, type: 'error' });
            setSubmitting(false);
        }
        } catch (error) {
            console.error("Network or parsing error:", error);
            setMessage({ text: `Network error: ${error.message}`, type: 'error' });
            setSubmitting(false);
        }
    };

    const fetchTeamEmails = async () => {
        const response = await fetch(`https://api.casedb.co/get_case_team.php?case_id=${case_id}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data.success) {
            setCaseTeamEmails(data.team_roles);
        }
    }

    useEffect(() => {
        fetchDeadlines();
        fetchTeamEmails();
    }, [case_id]);

    const resetDeadlineModal = () => {
        setCreatingDeadline(false);
        setEditingDeadline(false);
        setDeadline({
            users: [],
            subject: '',
            date: '',
            time: '',
            endDate: '',
            endTime: '',
            notes: '',
            isAllDay: true,
            reminders: [],
            defaultReminders: true,
            files: [],
            isDraft: false,
            draft_id: null,
            calendarStatus: 'pushed'
        });
    };

    return (
        <div className='case-section'>
            <div className='toc-header'>
                <h3>Calendar</h3>
                <div className='toc-header-actions'>
                    <button className="action" onClick={() => setCreatingDeadline(true)}>
                        Create
                    </button>
                </div>
            </div>
            <div className='deadlines'>
                <div className='deadline-calendar'>
                    <div className='calendar-header'>
                        <h4>
                            <div
                                className='calendar-arrow'
                                title={new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
                                onClick={() =>
                                    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
                                }
                            >
                                <ArrowLeft size={18} />
                            </div>
                            <span className='calendar-date-header'>
                                {currentMonth.toLocaleString('default', { month: 'long' })} {currentMonth.getFullYear()}
                            </span>
                            <div
                                className='calendar-arrow'
                                title={new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
                                onClick={() =>
                                    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
                                }
                            >
                                <ArrowRight size={18} />
                            </div>
                        </h4>
                    </div>
                    <div className='calendar-body'>
                        {daysInMonth.map((day, index) => (
                            <div
                                key={index}
                                className={`calendar-day ${day.dayNumber ? 'has-day' : 'has-no-day'} ${day.date === new Date().toLocaleDateString('en-CA') ? 'today' : ''} ${getEntriesForDate(day.date).length > 0 ? 'has-deadline' : ''} ${activeDay === day.date ? 'active-day' : ''}`}
                                onClick={() => setActiveDay(day.date === activeDay ? null : day.date)}
                            >
                                {day.blank ? (
                                    <div className='day-blank'>&nbsp;</div>
                                ) : (
                                    <>
                                        {getEntriesForDate(day?.date).length > 0
                                            ? <span className='deadline-noti'>{getEntriesForDate(day?.date).length}</span>
                                            : null}
                                        <div className='days-name'>
                                            {day.dayName}
                                        </div>
                                        <div className='days-date'>{day.dayNumber}</div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                {calendarEntries.length > 0 || undatedCalendarDrafts.length > 0 ? (
                    <div className='deadline-list'>
                        {activeDay ? (
                            <>
                                {/* Active Day Deadlines OR No Deadlines Message */}
                                {getEntriesForDate(activeDay).length > 0 ? (
                                    <>
                                        <div className='deadline-header'>
                                            <h4>
                                                {`Deadlines for ${new Date(activeDay + 'T00:00:00').toLocaleDateString(undefined, {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}`}
                                            </h4>
                                        </div>
                                        {getEntriesForDate(activeDay).map((entry, index) => renderCalendarEntry(entry, `active-${entry.event_uuid || entry.draft_id || index}`))}
                                    </>
                                ) : (
                                    <div className='deadline-header' style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <h4>
                                            {`No Deadlines for ${new Date(activeDay + 'T00:00:00').toLocaleDateString(undefined, {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}`}
                                        </h4>
                                        <div className='no-deadlines-message subtext'>
                                            <span className='subtext'>No deadlines on this date.</span>
                                        </div>
                                    </div>
                                )}
                                
                                {/* All Other Deadlines */}
                                {calendarEntries.filter(d => getEntryDate(d) !== activeDay).length > 0 && (
                                    <>
                                        <div className='deadline-header' style={{ marginTop: '20px', borderTop: '1px solid var(--border-color)' }}>
                                            <h4>All Other Deadlines</h4>
                                        </div>
                                        {calendarEntries.filter(d => getEntryDate(d) !== activeDay).map((entry, index) => renderCalendarEntry(entry, `other-${entry.event_uuid || entry.draft_id || index}`))}
                                    </>
                                )}
                                {undatedCalendarDrafts.length > 0 && (
                                    <>
                                        <div className='deadline-header' style={{ marginTop: '20px', borderTop: '1px solid var(--border-color)' }}>
                                            <h4>Drafts Needing Date</h4>
                                        </div>
                                        {undatedCalendarDrafts.map((entry, index) => renderCalendarEntry(entry, `undated-${entry.draft_id || index}`))}
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                {/* All Deadlines when no active day */}
                                <div className='deadline-header'>
                                    <h4>All Calendar Entries</h4>
                                </div>
                                {calendarEntries.map((entry, index) => renderCalendarEntry(entry, `all-${entry.event_uuid || entry.draft_id || index}`))}
                                {undatedCalendarDrafts.length > 0 && (
                                    <>
                                        <div className='deadline-header' style={{ marginTop: '20px', borderTop: '1px solid var(--border-color)' }}>
                                            <h4>Drafts Needing Date</h4>
                                        </div>
                                        {undatedCalendarDrafts.map((entry, index) => renderCalendarEntry(entry, `undated-${entry.draft_id || index}`))}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                ) : (
                    <div className='no-deadlines'>
                        <div className='deadline-header'>
                            <h4>No Calendar Entries Found</h4>
                        </div>
                        <div className='no-deadlines-message subtext'>
                            <span className='subtext'>No calendar entries to display.</span>
                        </div>
                    </div>
                )}
            </div>
            {(creatingDeadline || editingDeadline) && (
                <CalendarEntryModal
                    deadline={deadline}
                    setDeadline={setDeadline}
                    editingDeadline={editingDeadline}
                    submitting={submitting}
                    caseTeamEmails={caseTeamEmails}
                    calendarEntries={calendarEntries}
                    onSave={() => {
                        if (editingDeadline) handleUpdateDeadline();
                        else addDeadlineCalendarEvent();
                    }}
                    onDelete={() => handleDeleteDeadline(deadline.event_uuid)}
                    onClose={resetDeadlineModal}
                />
            )}
            {message && <Alert message={message.text} type={message.type} onClose={() => setMessage(null)} />}
        </div>
    );
}